import React from "react";
import { useEffect, useState } from "react";
import { useForm } from "../../useForm";

import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

import Input from "../../../controls/Input";
import TextArea from "../../../controls/TextArea";
import { ColorPicker } from "primereact/colorpicker";

const initialFValues = {
  name: "",
  description: "",
  is_permanent: 0,
  color: "",
};

export default function ProcessStatusForm(props: any) {
  const { index, status, setOpen, actionCall, isEdit } = props;

  const { values, setValues, handleInputChange } = useForm(
    initialFValues,
    false
  );

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      if (isEdit) {
        await actionCall(status, values, index);
      } else {
        await actionCall(values);
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (isEdit && status) {
      // normalize is_permanent to 0/1 so checkbox reflects correct state
      setValues({
        ...status,
        is_permanent: Number(status.is_permanent) ? 1 : 0,
      });
    }
  }, [status, setValues, isEdit]);

  return (
    <div className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-scroll py-6">
        <div className="px-4 sm:px-6">
          <div className="flex items-start justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              {isEdit ? "Edit " + status?.name : "Add new process status"}
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                onClick={() => setOpen({ childComponent: null, open: false })}
              >
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
        <div className="relative mt-6 flex-1 px-4 sm:px-6">
          <div>
            <div className="mt-2">
              <Input
                label="Name"
                name="name"
                onChange={handleInputChange}
                value={values.name}
                labelOn={true}
                type="text"
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
              />
              <div className="mt-4">
                <TextArea
                  label="Description"
                  name="description"
                  onChange={handleInputChange}
                  value={values.description}
                  labelOn={true}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                />
              </div>

              <div className="mt-4">
                <label
                  htmlFor="is_permanent"
                  className="block text-sm text-gray-700 mb-2"
                >
                  Permanently assign this status to products
                </label>
                <div className="flex items-center">
                  <input
                    id="is_permanent"
                    name="is_permanent"
                    type="checkbox"
                    checked={Number(values.is_permanent) === 1}
                    onChange={(e) =>
                      // useForm expects event.target.value; convert checked -> 1/0
                      handleInputChange({
                        target: {
                          name: "is_permanent",
                          value: e.target.checked ? 1 : 0,
                        },
                      } as any)
                    }
                    className="h-4 w-4 text-main-purple border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="color"
                  className="block text-sm font-medium text-gray-900"
                >
                  Color
                </label>
                <div className="mt-2 flex items-center space-x-3">
                  <ColorPicker
                    id="color"
                    value={values.color || ""}
                    onChange={(e) =>
                      handleInputChange({
                        target: { name: "color", value: e.value },
                      } as any)
                    }
                    format="hex"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:ring-gray-400"
          onClick={() => setOpen({ childComponent: null, open: false })}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isEdit ? "Saving..." : "Creating..."}
            </div>
          ) : isEdit ? (
            "Save"
          ) : (
            "Create"
          )}
        </button>
      </div>
    </div>
  );
}
